<script setup lang="ts">
/**
 * The cards that go on the tables.
 *
 * A venue in TABLE mode prints one of these per table, laminates it, and stops
 * having to walk over. This is the only thing in the product that produces one.
 *
 * THE VENUE TYPES ITS OWN TABLE NAMES, because there is no table registry to
 * pick from — `tableRef` is a free string everywhere else in this system, and
 * inventing an id here would mean inventing the table list to go with it.
 * Venues call their tables `12`, `T4`, `terasa 3` and `bar left`, and a form
 * that only accepted numbers would be wrong for most of them.
 *
 * THE CODES ARE MINTED BY THE SERVER, and that is not an implementation detail.
 * The console is a client of the public API — we dogfood it — so handing this
 * browser the key that mints table codes would put the venue's entire guest
 * surface in the devtools of a shared office laptop.
 *
 * WHAT IS PRINTED IS A URL, not a bare code, because a phone camera that finds
 * text offers to COPY it and one that finds a URL offers to OPEN it. When the
 * deployment has not been told where the guest app lives, this screen says so
 * instead of printing something nobody can scan.
 *
 * REPRINTING IS HOW YOU RETIRE A CARD. A laminated code cannot be recalled, so
 * every print mints fresh ones and the expiry is stated in words on the screen
 * — a card that silently stops working is the worst failure this feature has.
 */

import { computed, ref } from "vue";
import { qrSvg } from "@gf/qr";
import type { TableCards } from "../api";

const props = defineProps<{
  t: (key: string) => string;
  locale: string;
  cards: TableCards | null;
  busy: boolean;
  notice: string | null;
  /** The venue's session model. A CHECK-mode venue's cards will not open. */
  sessionMode: "CHECK" | "TABLE" | null;
}>();

const emit = defineEmits<{ print: [tables: readonly string[]] }>();

/**
 * One table per line, which is what a manager pastes out of a floor plan.
 *
 * Commas work too, because half of them will type `1, 2, 3` and being strict
 * about it would teach nobody anything.
 */
const input = ref("");

const tables = computed(() =>
  input.value
    .split(/[\n,]/)
    .map((t) => t.trim())
    .filter((t) => t !== ""),
);

const duplicates = computed(() => {
  const seen = new Set<string>();
  const twice = new Set<string>();
  for (const t of tables.value) {
    if (seen.has(t)) twice.add(t);
    seen.add(t);
  }
  return [...twice];
});

const dateOf = (iso: string): string =>
  new Intl.DateTimeFormat(props.locale, { dateStyle: "long" }).format(new Date(iso));

/** The QR itself. Inline SVG, so printing does not depend on a network. */
const svgFor = (card: { code: string; url: string | null }): string =>
  qrSvg(card.url ?? card.code);
</script>

<template>
  <section class="cards">
    <p class="cards__lede">{{ t("tableCards.lede") }}</p>

    <!--
      A venue that has not switched models yet. The cards are still printable —
      somebody is probably about to switch — and the screen says what will
      happen if they do not, rather than letting forty laminated cards answer
      "ask a server" at the table.
    -->
    <p v-if="sessionMode === 'CHECK'" class="cards__warn" data-mode-warning>
      {{ t("tableCards.modeWarning") }}
    </p>

    <form class="cards__form" @submit.prevent="emit('print', tables)">
      <label class="cards__label" for="tables">{{ t("tableCards.tablesLabel") }}</label>
      <textarea
        id="tables"
        v-model="input"
        class="cards__input"
        rows="6"
        :placeholder="t('tableCards.placeholder')"
        data-tables
      ></textarea>

      <p class="cards__hint">
        {{ t("tableCards.hint") }}
        <!-- Always a number: "Počet karet:" followed by nothing read as broken. -->
        <strong>{{ tables.length }}</strong>
      </p>
      <!--
        Named, rather than silently deduplicated. Two cards for table 12 is
        almost certainly a paste that went wrong, and a manager who is not told
        laminates both.
      -->
      <p v-if="duplicates.length > 0" class="cards__warn" data-duplicates>
        {{ t("tableCards.duplicates") }} {{ duplicates.join(", ") }}
      </p>

      <button
        type="submit"
        class="cards__go"
        data-print
        :disabled="busy || tables.length === 0"
      >
        {{ t("tableCards.print") }}
      </button>
    </form>

    <p v-if="notice" class="cards__warn" data-notice>{{ notice }}</p>

    <template v-if="cards">
      <p class="cards__expiry" data-expiry>
        {{ t("tableCards.expires") }} <strong>{{ dateOf(cards.expiresAt) }}</strong>
      </p>

      <!--
        No URL means the deployment has not been told where the guest app lives.
        The codes are still real and a card printed from one still scans as
        text, which is not good enough — said here rather than discovered at a
        table.
      -->
      <p v-if="cards.cards.some((card) => card.url === null)" class="cards__warn" data-no-url>
        {{ t("tableCards.noBaseUrl") }}
      </p>

      <ol class="cards__sheet">
        <li v-for="card in cards.cards" :key="card.tableRef" class="cards__card">
          <p class="cards__venue">{{ cards.venueName }}</p>
          <!-- eslint-disable-next-line vue/no-v-html -- qrSvg builds the markup -->
          <div class="cards__qr" v-html="svgFor(card)"></div>
          <p class="cards__table" data-table>{{ card.tableRef }}</p>
          <p class="cards__call">{{ t("tableCards.callToAction") }}</p>
        </li>
      </ol>
    </template>
  </section>
</template>

<style scoped>
.cards {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
}

.cards__lede,
.cards__hint,
.cards__expiry {
  margin: 0;
  color: var(--gf-color-text-muted);
}

/*
  The console's warning idiom, as `office.css` draws `gf-note--warn`: a tinted
  callout, full-contrast text. (It had a 3 px rule down the side until
  2026-09-25; a tint already reads as a state.)
*/
.cards__warn {
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-warning-surface);
  color: var(--gf-color-text);
}

.cards__form {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-1);
  max-width: 28rem;
}

.cards__label {
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

.cards__input {
  padding: var(--gf-space-2) var(--gf-space-3);
  border: var(--gf-border-thin) solid var(--gf-color-border);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface);
  color: var(--gf-color-text);
  font: inherit;
  resize: vertical;
}

/* Printing the cards is what this screen is for: its one primary fill. */
.cards__go {
  align-self: flex-start;
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-primary);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-primary);
  color: var(--gf-color-on-primary);
  font: inherit;
  font-weight: var(--gf-weight-semibold);
  box-shadow: var(--gf-shadow-glow);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.cards__go:hover:not(:disabled) {
  background: color-mix(in srgb, var(--gf-color-primary) 88%, var(--gf-color-text));
}

.cards__go:disabled {
  opacity: 0.5;
  cursor: default;
  box-shadow: none;
}

.cards__go:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.cards__sheet {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: var(--gf-space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.cards__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gf-space-1);
  padding: var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface);
  /* A card is cut out and laminated; it must not straddle a page break. */
  break-inside: avoid;
  text-align: center;
}

.cards__venue {
  margin: 0;
  font-weight: var(--gf-weight-semibold);
}

.cards__qr {
  width: 100%;
  max-width: 11rem;
}

.cards__qr :deep(svg) {
  width: 100%;
  height: auto;
  /*
    A QR needs a light quiet zone to scan. Forcing it here rather than
    inheriting the theme: a dark-mode back office that printed white-on-black
    cards would produce forty unscannable laminates.
  */
  background: #fff;
}

.cards__table {
  margin: 0;
  font-size: var(--gf-text-2xl);
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
  line-height: 1;
}

.cards__call {
  margin: 0;
  color: var(--gf-color-text-muted);
}

@media print {
  /* The form is not part of the card. */
  .cards__form,
  .cards__lede,
  .cards__hint {
    display: none;
  }
}

/*
 * A primary that cannot be pressed yet is NEUTRAL, not a faded red: a washed-out
 * oxblood still reads as "the button to press", and pink is not a state.
 */
.cards__go:disabled {
  color: var(--gf-color-text-muted);
  background: var(--gf-color-surface-active);
  border-color: transparent;
  box-shadow: none;
  opacity: 1;
}
</style>
